import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TopBar from "../../components/layout/TopBar";

const SECTIONS = [
  { id: "intro",     label: "Introduction" },
  { id: "workflow",  label: "Typical Workflow" },
  { id: "projects",  label: "Projects & Designs" },
  { id: "layout",    label: "Layout Tool" },
  { id: "editor",    label: "Design Editor" },
  { id: "summary",   label: "Summary Tab" },
  { id: "hydraulic", label: "Hydraulic Layout" },
  { id: "meco",      label: "Meridional Contour" },
  { id: "edges",     label: "Edges" },
  { id: "beta",      label: "Beta Angles" },
  { id: "thickness", label: "Thickness" },
  { id: "transform", label: "Transform" },
  { id: "views",     label: "2D & 3D Views" },
  { id: "curvature", label: "Curvature" },
  { id: "cutoff",    label: "Cutoff" },
  { id: "export",    label: "Export" },
  { id: "assembly",  label: "Assembly" },
  { id: "copilot",   label: "Pump Copilot" },
  { id: "library",   label: "Component Library" },
  { id: "shortcuts", label: "Keyboard Shortcuts" },
  { id: "admin",     label: "Admin Panel" },
];

function SectionTitle({ icon, children }) {
  return (
    <h2 className="flex items-center gap-2.5 text-base font-semibold text-gray-900 mb-4 mt-8 pb-3 border-b border-gray-200 first:mt-0">
      <span className="w-7 h-7 bg-bh-600 rounded-lg flex items-center justify-center text-white text-sm shrink-0">{icon}</span>
      {children}
    </h2>
  );
}

function DocCard({ children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 overflow-x-auto">
      {children}
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="bg-bh-50 border-l-4 border-bh-500 rounded-r-xl px-4 py-2.5 my-3 text-sm text-gray-700">
      <span className="font-semibold text-bh-700">Tip: </span>{children}
    </div>
  );
}

function Warn({ children }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl px-4 py-2.5 my-3 text-sm text-gray-700">
      <span className="font-semibold text-amber-700">Note: </span>{children}
    </div>
  );
}

function Key({ children }) {
  return (
    <kbd className="font-mono text-xs bg-gray-800 text-gray-100 rounded px-1.5 py-0.5 border border-gray-700 shadow-sm">{children}</kbd>
  );
}

function ParamTable({ rows }) {
  if (!rows?.length) return null;
  const headers = Object.keys(rows[0]);
  return (
    <table className="w-full text-sm border-collapse min-w-max">
      <thead>
        <tr className="bg-gray-50">
          {headers.map(h => (
            <th key={h} className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200 whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
            {headers.map((h, j) => (
              <td key={h} className={`px-3 py-2.5 text-sm align-top ${j === 0 ? "font-mono text-xs font-semibold text-bh-700 whitespace-nowrap" : "text-gray-600"}`}>{row[h]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Steps({ items }) {
  return (
    <ol className="space-y-2.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="w-5 h-5 rounded-full bg-bh-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
          <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
        </li>
      ))}
    </ol>
  );
}

export default function DocsPage() {
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState("intro");
  const location = useLocation();

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el && contentRef.current) {
      contentRef.current.scrollTo({ top: el.offsetTop - 24, behavior: "smooth" });
    }
  };

  // Scroll to hash section when navigated from the sidebar flyout
  useEffect(() => {
    const hash = location.hash?.slice(1);
    if (!hash) return;
    // Brief delay to ensure refs are populated after render
    const t = setTimeout(() => scrollTo(hash), 80);
    return () => clearTimeout(t);
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const onScroll = () => {
      const top = container.scrollTop;
      let current = SECTIONS[0].id;
      for (const { id } of SECTIONS) {
        const el = sectionRefs.current[id];
        if (el && el.offsetTop - 60 <= top) current = id;
      }
      setActiveSection(current);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  const ref = (id) => (el) => { sectionRefs.current[id] = el; };

  return (
    <div className="flex flex-col h-full">
      <TopBar title="Documentation" />
      <div className="flex flex-1 min-h-0">

        {/* ── Left nav ───────────────────────── */}
        <aside className="w-52 shrink-0 bg-white border-r border-gray-200 overflow-y-auto py-3">
          <nav className="px-2 space-y-0.5">
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  activeSection === id
                    ? "bg-bh-50 text-bh-700 font-semibold border-l-2 border-bh-600 pl-2.5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Content ────────────────────────── */}
        <main ref={contentRef} className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-3xl mx-auto px-8 py-8">

            {/* Hero */}
            <div className="bg-gray-900 rounded-2xl p-7 mb-8 relative overflow-hidden">
              <div className="absolute right-0 top-0 w-64 h-full opacity-20"
                   style={{ background: "radial-gradient(ellipse at right, #3b82f6 0%, transparent 70%)" }} />
              <div className="relative">
                <span className="inline-block bg-bh-600 text-white text-xs font-mono px-3 py-0.5 rounded-full mb-3">v1.0 · July 2025</span>
                <h1 className="text-2xl font-bold text-white tracking-tight mb-2">FastBlade Documentation</h1>
                <p className="text-gray-400 text-sm">Web-based platform for centrifugal pump and hydraulic turbine design.</p>
              </div>
            </div>

            {/* Introduction */}
            <section ref={ref("intro")} id="intro">
              <SectionTitle icon="⚡">Introduction</SectionTitle>
              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                FastBlade streamlines the parametric design of pump impellers and diffusors — from initial hydraulic sizing through to export-ready 3D geometry. All design data is stored server-side and accessible from any browser without local installation.
              </p>

              <h3 className="text-sm font-semibold text-gray-800 mb-2">Signing In</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-1">
                Navigate to your FastBlade URL and sign in with your email and password, or use <strong>Microsoft Single Sign-On</strong> if your organisation has it configured. After authentication you land on the Dashboard.
              </p>
              <Tip>Your session is preserved across browser tabs. Closing a tab does not sign you out.</Tip>
            </section>

            {/* Typical Workflow */}
            <section ref={ref("workflow")} id="workflow">
              <SectionTitle icon="🗺">Typical Workflow</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">A blade design follows this general sequence. Each step is independent — you can return to any tab at any time.</p>
              <div className="flex flex-wrap gap-1.5 items-center mb-6">
                {["Create Project","Set Duty Point","Draw MECO","Define Edges","Set Beta / Thickness","Review 3D","Release & Export"].map((s, i, arr) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">{s}</span>
                    {i < arr.length - 1 && <span className="text-gray-400 text-xs">→</span>}
                  </span>
                ))}
              </div>
            </section>

            {/* Projects & Designs */}
            <section ref={ref("projects")} id="projects">
              <SectionTitle icon="📁">Projects & Designs</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Every design lives inside a <strong className="text-gray-800">Project</strong>. A project holds the pump duty point, machine type, and ERP reference, and can contain multiple blade designs — useful for design variants or stage configurations.</p>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Creating a Project</h3>
              <Steps items={[
                "Open Projects from the left navigation and click New Project.",
                "Enter a name, optional description, and choose a Machine Type (OH, VS, BB1–BB5).",
                "Click Create. You land on the Project Detail page.",
              ]} />
              <h3 className="text-sm font-semibold text-gray-800 mb-2 mt-5">Managing Designs</h3>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">From the Project Detail page you can create new designs, open existing ones, compare meridional contours with the MECO overlay, filter by status or topology, and copy designs from other projects.</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Draft</div>
                  <p className="text-xs text-gray-600 leading-relaxed">Editable. Only the assigned designer can check out and modify. Others see it read-only.</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Released</div>
                  <p className="text-xs text-gray-600 leading-relaxed">Locked for changes. Use Un-release (admin only) if post-release edits are needed.</p>
                </div>
              </div>
              <Warn>A design can only be edited by one person at a time. When you open a design in edit mode it is checked out to you — others see a lock icon. Always save and check in when you are done.</Warn>
            </section>

            {/* Layout Tool */}
            <section ref={ref("layout")} id="layout">
              <SectionTitle icon="🎯">Layout Tool</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Sets the hydraulic duty point for a project. Open via <strong className="text-gray-800">Projects → [project] → Open Layout Tool</strong>.</p>
              <DocCard>
                <ParamTable rows={[
                  { Parameter: "n",        Unit: "rpm",   Description: "Rotational speed" },
                  { Parameter: "Q",        Unit: "m³/s",  Description: "Volume flow rate" },
                  { Parameter: "H",        Unit: "m",     Description: "Total head (single stage)" },
                  { Parameter: "n_stages", Unit: "—",     Description: "Number of stages; H is divided equally across stages" },
                  { Parameter: "variant",  Unit: "—",     Description: "Statistical curve variant: Standard / Progressive / Conservative" },
                ]} />
              </DocCard>
              <p className="text-sm text-gray-600 leading-relaxed">Once saved, the Hydraulic Layout tab in every design within this project shows predesign results derived from these values.</p>
            </section>

            {/* Design Editor */}
            <section ref={ref("editor")} id="editor">
              <SectionTitle icon="✏️">Design Editor Overview</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Clicking a design opens the Design Editor with a tab bar across the top for all geometry stages. The Pump Copilot panel slides in from the right edge.</p>
              <DocCard>
                <div className="divide-y divide-gray-100">
                  {[
                    ["Summary",            "Global design parameters, release controls, design lineage"],
                    ["Hydraulic Layout",   "Predesign output derived from the project duty point"],
                    ["Meridional Contour", "Hub / shroud / streamline contour definition (MECO)"],
                    ["Edges",              "Leading- and trailing-edge coordinates per construction line"],
                    ["Beta Angles",        "Blade angle distribution along each construction line"],
                    ["Thickness",          "Blade thickness distribution"],
                    ["Transform",          "Global rotation, mirroring, and axial offset"],
                    ["2D View",            "Blade-to-blade unrolled surface view"],
                    ["3D View",            "Interactive 3D surface render"],
                    ["Curvature",          "Gaussian and mean curvature colour map"],
                    ["Cutoff",             "Meridional cutoff plane definition"],
                    ["Export",             "DOCX, XML, ZIP, STEP, and GeomTurbo downloads"],
                  ].map(([tab, desc]) => (
                    <div key={tab} className="flex gap-4 py-2.5">
                      <span className="font-mono text-xs font-semibold text-bh-700 w-36 shrink-0 mt-0.5 leading-relaxed">{tab}</span>
                      <span className="text-sm text-gray-600 leading-relaxed">{desc}</span>
                    </div>
                  ))}
                </div>
              </DocCard>
              <Tip>Press <Key>Ctrl</Key> + <Key>S</Key> (or <Key>⌘ S</Key> on Mac) on any tab to save. A confirmation toast appears.</Tip>
            </section>

            {/* Summary */}
            <section ref={ref("summary")} id="summary">
              <SectionTitle icon="📋">Summary Tab</SectionTitle>
              <DocCard>
                <ParamTable rows={[
                  { Parameter: "n_blades",          Typical: "5 – 9",    Effect: "Number of full blades; determines blade-to-blade spacing." },
                  { Parameter: "n_samples",          Typical: "30 – 100", Effect: "Streamwise sample points per construction line. Higher = smoother surfaces." },
                  { Parameter: "topology",           Typical: "RoundEdge",Effect: "RoundEdge fillets the LE/TE; StraightEdge clips sharply." },
                  { Parameter: "rotation_direction", Typical: "CCW / CW", Effect: "Determines blade wrap direction when unrolling to 2D." },
                  { Parameter: "spanwise_degree",    Typical: "2 – 4",    Effect: "Polynomial degree for spanwise B-spline interpolation." },
                  { Parameter: "density_factor",     Typical: "0.0 – 1.0",Effect: "Cosine clustering: 0 = uniform sampling, 1 = max LE/TE clustering." },
                ]} />
              </DocCard>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Release Workflow</h3>
              <Steps items={[
                "Save all geometry tabs before releasing.",
                "Click Release Design on the Summary tab and confirm the dialog.",
                "The design becomes read-only and the release is logged with your name and timestamp.",
              ]} />
              <p className="text-sm text-gray-600 mt-3 leading-relaxed">The <strong className="text-gray-800">lineage chart</strong> at the bottom shows the derivation tree — which designs were copied from which predecessors. Hover a node for author and date details.</p>
            </section>

            {/* Hydraulic Layout */}
            <section ref={ref("hydraulic")} id="hydraulic">
              <SectionTitle icon="📐">Hydraulic Layout Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Displays predesign results computed from the project's Layout Tool configuration. Updates automatically when the duty point changes — no input is required here.</p>
              <DocCard>
                <ParamTable rows={[
                  { Symbol: "nq",  Description: "Specific speed (rpm, m³/s, m) — determines machine type" },
                  { Symbol: "D2",  Description: "Impeller outlet diameter" },
                  { Symbol: "D1",  Description: "Impeller inlet eye diameter" },
                  { Symbol: "b2",  Description: "Outlet width" },
                  { Symbol: "β₂",  Description: "Outlet blade angle (statistical)" },
                  { Symbol: "u2",  Description: "Peripheral velocity at outlet" },
                  { Symbol: "φ₁",  Description: "Flow coefficient at inlet" },
                  { Symbol: "ψ₂",  Description: "Head coefficient" },
                ]} />
              </DocCard>
              <Tip>FastBlade automatically selects Radial (nq &lt; 70) or Mixed-Flow (nq ≥ 70) and adjusts statistical curves accordingly.</Tip>
            </section>

            {/* MECO */}
            <section ref={ref("meco")} id="meco">
              <SectionTitle icon="〰️">Meridional Contour Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Define the axisymmetric flow-path shape using <em>construction lines</em> — one for HUB, one for SHROUD, and up to three intermediate streamlines (SL-1, SL-2, SL-3).</p>
              <ul className="text-sm text-gray-600 space-y-2 mb-4 pl-5 list-disc leading-relaxed">
                <li><strong className="text-gray-800">Manual entry</strong> — type r–z coordinates (mm) into the table.</li>
                <li><strong className="text-gray-800">Paste from Excel</strong> — copy two columns (r, z) and paste directly. Accepts comma and period decimal separators.</li>
                <li><strong className="text-gray-800">B-Spline mode</strong> — parametric control via LE/TE positions and tangent angles for a smoother, controlled contour.</li>
                <li><strong className="text-gray-800">Interpolated mode</strong> — intermediate streamlines are computed automatically between HUB and SHROUD.</li>
              </ul>
              <Tip>Each construction line has a <code className="font-mono text-xs bg-gray-100 px-1 rounded">theta_start</code> offset that rotates the blade wrap start point — useful for aligning LE positions across hub and shroud.</Tip>
            </section>

            {/* Edges */}
            <section ref={ref("edges")} id="edges">
              <SectionTitle icon="🔧">Edges Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Define the leading edge (LE) and trailing edge (TE) positions for each construction line in the meridional plane.</p>
              <DocCard>
                <ParamTable rows={[
                  { Field: "LE_r, LE_z", Unit: "mm", Description: "Leading edge coordinates in the meridional r–z plane" },
                  { Field: "TE_r, TE_z", Unit: "mm", Description: "Trailing edge coordinates" },
                ]} />
              </DocCard>
              <p className="text-sm text-gray-600 leading-relaxed">In B-Spline mode, LE and TE are derived from spline parameters and are read-only (shown with a lock label). In Interpolated mode, intermediate streamlines inherit values interpolated between HUB and SHROUD.</p>
            </section>

            {/* Beta */}
            <section ref={ref("beta")} id="beta">
              <SectionTitle icon="📈">Beta Angles Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Define the blade wrap angle distribution β(m) along the normalised meridional arc length (m = 0 at LE, m = 1 at TE) for each construction line.</p>
              <ul className="text-sm text-gray-600 space-y-2 mb-4 pl-5 list-disc leading-relaxed">
                <li>Drag control points directly on the interactive spline chart.</li>
                <li>Edit values numerically in the m–β table on the left.</li>
                <li><strong className="text-gray-800">Paste from Excel</strong> — two-column paste (m | β), validated that m runs 0 → 1.</li>
                <li>Set the B-spline <strong className="text-gray-800">degree</strong> (1 = linear, up to 4 = high-order smooth).</li>
                <li>Toggle <strong className="text-gray-800">Fix End</strong> to lock β at the trailing edge.</li>
              </ul>
              <Tip>β is the blade angle relative to the tangential direction. At the LE it equals the inlet flow angle for shock-free entry; at the TE it matches β₂ from hydraulic sizing.</Tip>
            </section>

            {/* Thickness */}
            <section ref={ref("thickness")} id="thickness">
              <SectionTitle icon="📏">Thickness Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Define the blade thickness distribution t(m) along the same normalised arc. The spline editor and Excel paste work identically to the Beta Angles tab.</p>
              <Warn>With <em>RoundEdge</em> topology, thickness at m = 0 and m = 1 is forced to zero — the edge is smoothly rounded by the kernel. With <em>StraightEdge</em>, the full value at LE/TE is applied.</Warn>
            </section>

            {/* Transform */}
            <section ref={ref("transform")} id="transform">
              <SectionTitle icon="↔️">Transform Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Apply global geometric transforms to the assembled blade row. Transforms are applied on export; they do not alter MECO or edge data.</p>
              <DocCard>
                <ParamTable rows={[
                  { Parameter: "Rotation", Description: "Rotate the blade about the z-axis (degrees)" },
                  { Parameter: "Mirror X", Description: "Mirror about the r–z plane — for opposite-handed impellers" },
                  { Parameter: "Mirror Z", Description: "Mirror about the r–θ plane — when the axial direction is reversed" },
                  { Parameter: "Z Offset", Description: "Translate axially — for positioning within an assembly" },
                ]} />
              </DocCard>
            </section>

            {/* Views */}
            <section ref={ref("views")} id="views">
              <SectionTitle icon="🔷">2D & 3D Views</SectionTitle>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">2D View — Blade-to-Blade</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Shows the blade unrolled onto a cylindrical surface at each streamline (θ vs. m). Use this to inspect wrap angle, LE/TE alignment, and any unintended inflections in the angle distribution.</p>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">3D View</h3>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">Interactive Plotly 3D surface render. Rotate, zoom, and pan with the mouse. All <code className="font-mono text-xs bg-gray-100 px-1 rounded">n_blades</code> are shown at the correct angular pitch. Save a screenshot via the camera icon in the toolbar.</p>
              <Tip>The 3D view recomputes when you save any geometry tab. If the blade looks unexpected, check for missing edge coordinates or an invalid Beta distribution.</Tip>
            </section>

            {/* Curvature */}
            <section ref={ref("curvature")} id="curvature">
              <SectionTitle icon="🌊">Curvature Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">Computes and renders Gaussian and mean curvature of the blade surface as a colour map. High-curvature regions indicate sharp bends; inflection lines are highlighted automatically.</p>
              <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc leading-relaxed">
                <li>Spot unwanted S-shaped curvature in the blade passage.</li>
                <li>Verify smooth leading-edge transitions.</li>
                <li>Identify potential high-stress regions before CFD.</li>
              </ul>
            </section>

            {/* Cutoff */}
            <section ref={ref("cutoff")} id="cutoff">
              <SectionTitle icon="✂️">Cutoff Tab</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Define a meridional cutoff plane — a line in the r–z plane that trims the blade geometry for outlet cutoffs or shroud disc cutoffs.</p>
              <DocCard>
                <ParamTable rows={[
                  { Field: "R1, Z1", Unit: "mm", Description: "Start point of the cutoff line" },
                  { Field: "R2, Z2", Unit: "mm", Description: "End point of the cutoff line" },
                  { Field: "Active", Unit: "—",  Description: "Toggle to include / exclude the cutoff from export" },
                ]} />
              </DocCard>
            </section>

            {/* Export */}
            <section ref={ref("export")} id="export">
              <SectionTitle icon="📦">Export Tab</SectionTitle>
              <DocCard>
                <ParamTable rows={[
                  { Format: ".docx",      "Use case": "Engineering report with parameters, plots, and metadata. Ready for client delivery.",   Prerequisite: "Any saved design" },
                  { Format: ".xml",       "Use case": "Full parametric design serialisation — can be re-imported.",                             Prerequisite: "Any saved design" },
                  { Format: ".zip",       "Use case": "Geometry point cloud for all construction lines.",                                      Prerequisite: "3D geometry computed" },
                  { Format: ".stp",       "Use case": "STEP solid model for CAD import.",                                                      Prerequisite: "3D geometry computed" },
                  { Format: "GeomTurbo",  "Use case": "Numeca / FINE™ Turbo mesh generation input file.",                                     Prerequisite: "3D geometry computed" },
                ]} />
              </DocCard>
              <Warn>STEP and GeomTurbo exports require 3D geometry to have been computed. If a download button is greyed out, open the 3D View and wait for the surface to render first.</Warn>
            </section>

            {/* Assembly */}
            <section ref={ref("assembly")} id="assembly">
              <SectionTitle icon="🔩">Multi-Row Assembly</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Groups multiple blade rows (e.g. impeller + diffusor) into a single stage configuration for a combined 3D view and meridional overlay.</p>
              <Steps items={[
                "Open Assemblies from the sidebar and click New Assembly.",
                "Click Add Member, select an existing design, and assign a position index (1 = innermost row).",
                "Set a Z Offset for each member to position rows axially relative to each other.",
                "Optionally apply Mirror Z or Mirror X for downstream-facing rows.",
                "The 3D Assembly View updates automatically showing all blade rows in position.",
              ]} />
              <p className="text-sm text-gray-600 mt-4 leading-relaxed">The <strong className="text-gray-800">Combined MECO panel</strong> overlays hub and shroud contours of all member rows in a single r–z plot — for visual inspection of passage continuity between impeller outlet and diffusor inlet.</p>
            </section>

            {/* Copilot */}
            <section ref={ref("copilot")} id="copilot">
              <SectionTitle icon="🤖">Pump Copilot</SectionTitle>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">An AI assistant specialised in centrifugal pump and turbomachinery engineering. It is automatically aware of the currently open design and project context.</p>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">Click the <strong className="text-gray-800">sparkle icon (✨)</strong> in the top-right corner to open the side panel. Resize it by dragging its left edge.</p>
              <ul className="text-sm text-gray-600 space-y-1.5 mb-4 pl-5 list-disc leading-relaxed">
                <li>Answer questions about hydraulic theory — Euler equation, velocity triangles, specific speed.</li>
                <li>Help size inlet and outlet dimensions for a given duty point.</li>
                <li>Explain the effect of individual design parameters on pump performance.</li>
                <li>Retrieve relevant excerpts from your organisation's <strong className="text-gray-800">Knowledge Base</strong>.</li>
                <li>Suggest follow-up questions after each answer.</li>
              </ul>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">Use the <strong className="text-gray-800">paperclip icon</strong> to attach a .txt, .csv, or .xml file — the Copilot reads its content as context, useful for CFD output or data tables.</p>
              <Warn>The Copilot is a design aid, not a replacement for engineering judgment. Always verify computed values against established references before use in manufacturing.</Warn>
            </section>

            {/* Library */}
            <section ref={ref("library")} id="library">
              <SectionTitle icon="📚">Component Library</SectionTitle>
              <p className="text-sm text-gray-600 leading-relaxed">Stores reusable design definitions that can be referenced across projects. Access via <strong className="text-gray-800">Designs</strong> in the left navigation. Designs promoted to the library appear here as read-only reference components — useful for standardised impeller families or validated stage configurations.</p>
            </section>

            {/* Shortcuts */}
            <section ref={ref("shortcuts")} id="shortcuts">
              <SectionTitle icon="⌨️">Keyboard Shortcuts</SectionTitle>
              <div className="bg-white border border-gray-200 rounded-xl px-5 divide-y divide-gray-100">
                {[
                  ["Save current tab",                                  <><Key>Ctrl</Key> + <Key>S</Key></>],
                  ["Save on Mac",                                       <><Key>⌘</Key> + <Key>S</Key></>],
                  ["New line in Copilot message",                       <><Key>Shift</Key> + <Key>Enter</Key></>],
                  ["Send Copilot message",                              <Key>Enter</Key>],
                  ["Paste table from Excel (MECO / Beta / Thickness)",  <><Key>Ctrl</Key> + <Key>V</Key> <span className="text-gray-500 text-xs">in table</span></>],
                ].map(([label, keys], i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="text-gray-700">{label}</span>
                    <span className="flex items-center gap-1">{keys}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Admin */}
            <section ref={ref("admin")} id="admin">
              <SectionTitle icon="⚙️">Admin Panel</SectionTitle>
              <div className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-lg mb-4">
                🔒 Visible to Admin users only
              </div>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Accessible via <strong className="text-gray-800">Admin</strong> in the left navigation for users with the Admin role.</p>

              <h3 className="text-sm font-semibold text-gray-800 mb-2">User Management</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Create, edit, and deactivate user accounts. Assign users to an Entity (organisation unit) and toggle admin privileges per user.</p>

              <h3 className="text-sm font-semibold text-gray-800 mb-2">Entities</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">Each entity has its own colour palette, logo, and app name. The application automatically applies the logged-in user's entity theme at sign-in.</p>

              <h3 className="text-sm font-semibold text-gray-800 mb-2">LLM Provider</h3>
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">Configure the AI provider for the Pump Copilot under <strong className="text-gray-800">Admin → Settings → LLM Provider</strong>. Supported providers:</p>
              <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc mb-4">
                <li><strong className="text-gray-800">OpenAI</strong> — GPT-4o, GPT-4o mini</li>
                <li><strong className="text-gray-800">Anthropic</strong> — Claude Sonnet, Claude Haiku</li>
                <li><strong className="text-gray-800">Azure OpenAI</strong> — self-managed deployment</li>
                <li><strong className="text-gray-800">Mistral</strong></li>
              </ul>
              <Tip>Click <strong>Test connection</strong> after entering the API key before saving to verify connectivity.</Tip>

              <h3 className="text-sm font-semibold text-gray-800 mb-2 mt-5">Knowledge Base</h3>
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">Upload PDF or text documents under <strong className="text-gray-800">Admin → Knowledge Base</strong> to build the Copilot's retrieval context.</p>
              <Steps items={[
                "Click Upload Document and select a PDF, TXT, or markdown file.",
                "The document is chunked and embedded automatically. Status changes from Pending to Indexed.",
                "The Copilot now retrieves relevant excerpts from this document when answering questions.",
              ]} />

              <h3 className="text-sm font-semibold text-gray-800 mb-2 mt-5">Statistical Curves</h3>
              <p className="text-sm text-gray-600 leading-relaxed">The predesign calculator uses statistical curves (ψ₂ vs. nq, φ₁ vs. nq) stored in the database. Update them under <strong className="text-gray-800">Admin → Stat Curves</strong> to calibrate predictions against proprietary pump test data.</p>
            </section>

          </div>
        </main>
      </div>
    </div>
  );
}
